import React, { useState, useEffect } from 'react';
import { compressFiles, optimizeFormData, validateFormData } from '../../../shared/utils/performanceUtils';
import { apiGet, apiPost, apiPut, apiDelete } from '../../../shared/utils/apiConfig';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaSave, FaPlus, FaTrash, FaImage } from 'react-icons/fa';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PharmacyEditProduct = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [subCategories, setSubCategories] = useState([]);
    const [units, setUnits] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        sub_category_id: '',
        images: [],
        image_url: ''
    });
    const [variants, setVariants] = useState([
        { unit_id: '', price: '', quantity: '' }
    ]);
    const [existingImages, setExistingImages] = useState([]);
    const [selectedImagesToDelete, setSelectedImagesToDelete] = useState([]);
    const [productData, setProductData] = useState(null);

    // أنواع الوحدات لم تعد مطلوبة - سيتم جلب قائمة الوحدات من /category_unit

    useEffect(() => {
        setTitle('تعديل المنتج');
        if (id) {
            const loadData = async () => {
                await fetchSubCategories();
                await fetchUnits(); // تحميل الوحدات من /category_unit
                await fetchProductDetails();
            };
            loadData();
        }
    }, [id, setTitle]);

    // Debug: مراقبة تغييرات subCategories
    useEffect(() => {
        
        // إذا تم تحميل subCategories وكان هناك product data، نحدث sub_category_id
        if (subCategories.length > 0 && productData) {
            const matchingCategory = subCategories.find(cat => 
                cat.name === productData.subcategory || 
                cat.name_ar === productData.subcategory
            );
            
            if (matchingCategory && formData.sub_category_id !== matchingCategory.id) {
                setFormData(prev => ({
                    ...prev,
                    sub_category_id: matchingCategory.id
                }));
            }
        }
    }, [subCategories, productData]);



    const getAuthToken = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.token;
            } catch (error) {
                return null;
            }
        }
        return null;
    };

    const fetchProductDetails = async () => {
        try {
            setLoading(true);
            const token = getAuthToken();
            
            if (!token) {
                toast.error('لا يوجد token للمصادقة');
                navigate('/pharmacy-products');
                return;
            }

            const response = await axios.get(`${BASE_URL}/pharmacy/products/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });


            if (response.data.status === 200) {
                const product = response.data.data;
                setProductData(product); // تخزين product data

                // البحث عن subcategory_id بناءً على اسم الفئة
                let subcategoryId = '';
                if (product.subcategory && typeof product.subcategory === 'string') {
                    // البحث في subCategories عن الفئة التي تطابق الاسم
                    const matchingCategory = subCategories.find(cat => 
                        cat.name === product.subcategory || 
                        cat.name_ar === product.subcategory
                    );
                    if (matchingCategory) {
                        subcategoryId = matchingCategory.id;
                    }
                } else if (product.subcategory?.id) {
                    subcategoryId = product.subcategory.id;
                }

                setFormData({
                    name: product.name || '',
                    description: product.description || '',
                    sub_category_id: subcategoryId,
                    images: [],
                    image_url: product.image_url || product.first_image_url || ''
                });


                // تحديث existingImages مع الهيكل الصحيح للصور
                const imagesWithIds = (product.images || []).map((image) => ({
                    id: image.id, // استخدام id الفعلي من الـ API
                    url: image.url
                }));
                setExistingImages(imagesWithIds);
                
                if (product.units && product.units.length > 0) {
                    setVariants(product.units.map(unit => ({
                        unit_id: unit.id || unit.unit_id || '',
                        price: unit.price || '',
                        quantity: unit.quantity || ''
                    })));
                }
            } else {
                throw new Error(response.data.message || 'فشل في جلب تفاصيل المنتج');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || 'فشل في جلب تفاصيل المنتج');
            navigate('/pharmacy-products');
        } finally {
            setLoading(false);
        }
    };

    const fetchSubCategories = async () => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/pharmacy/sub-categories`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });


            // التحقق من وجود البيانات مباشرة
            if (response.data.data && Array.isArray(response.data.data)) {
                setSubCategories(response.data.data);
            } else if (response.data.status === 'success' || response.data.status === 200) {
                setSubCategories(response.data.data || []);
            } else {

            }
        } catch (error) {
            toast.error('فشل في جلب الفئات الفرعية');
        }
    };

    const fetchUnits = async () => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/category_unit`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            const list = response.data?.data || response.data?.result || [];
            setUnits(Array.isArray(list) ? list : []);
        } catch (error) {
            toast.error('فشل في جلب الوحدات');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'image_url') {
            const trimmed = value.trim();
            setFormData(prev => ({
                ...prev,
                image_url: value,
                images: trimmed ? [] : prev.images
            }));
            if (trimmed) {
                setExistingImages([]);
                setSelectedImagesToDelete([]);
            }
            return;
        }
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        setFormData(prev => ({
            ...prev,
            image_url: '',
            images: [...prev.images, ...files]
        }));
    };

    const removeImage = (index) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
    };

    const handleImageDeleteSelection = (imageId, isChecked) => {
        if (isChecked) {
            setSelectedImagesToDelete(prev => [...prev, imageId]);
        } else {
            setSelectedImagesToDelete(prev => prev.filter(id => id !== imageId));
        }
    };

    const handleVariantChange = (index, field, value) => {
        const newVariants = [...variants];
        newVariants[index][field] = value;

        setVariants(newVariants);
    };

    const addVariant = () => {
        setVariants([...variants, { unit_id: '', price: '', quantity: '' }]);
    };

    const removeVariant = (index) => {
        if (variants.length > 1) {
            setVariants(variants.filter((_, i) => i !== index));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim() || !formData.description.trim() || !formData.sub_category_id) {
            toast.error('يرجى ملء جميع الحقول المطلوبة');
            return;
        }

        if (variants.some(v => !v.unit_id || !v.price || !v.quantity)) {
            toast.error('يرجى ملء جميع بيانات المتغيرات');
            return;
        }

        try {
            setSubmitting(true);
            const token = getAuthToken();

            if (!token) {
                toast.error('لا يوجد token للمصادقة');
                return;
            }

            const submitData = new FormData();
            submitData.append('name', formData.name.trim());
            submitData.append('description', formData.description.trim());
            submitData.append('sub_category_id', formData.sub_category_id);
            const imageUrlTrimmed = String(formData.image_url || '').trim();
            if (imageUrlTrimmed) {
                submitData.append('image_url', imageUrlTrimmed);
            }

            // إضافة الصور الجديدة
            formData.images.forEach((image, index) => {
                submitData.append(`images[${index}]`, image);
            });

            // إضافة الصور المحددة للحذف
            selectedImagesToDelete.forEach((imageId, index) => {
                submitData.append(`images_to_delete[${index}]`, imageId);
            });

            // إضافة المتغيرات
            variants.forEach((variant, index) => {
                submitData.append(`variants[${index}][unit_id]`, variant.unit_id || '');
                submitData.append(`variants[${index}][price]`, variant.price || '');
                submitData.append(`variants[${index}][quantity]`, variant.quantity || '');
            });

          

            const response = await axios.post(`${BASE_URL}/pharmacy/products/${id}`, submitData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                    'Content-Type': 'multipart/form-data',
                },
            });


            if (response.data.status === 'failed' || response.data.status === 'faild') {
                throw new Error(response.data.message || 'فشل في تعديل المنتج');
            }

            toast.success('تم تعديل المنتج بنجاح');
            navigate('/pharmacy-products');
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || 'فشل في تعديل المنتج');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل البيانات...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-outline-secondary d-flex align-items-center"
                        onClick={() => navigate('/pharmacy-products')}
                        style={{ borderRadius: '8px' }}>
                        <FaArrowLeft className="mx-2" />
                        العودة
                    </motion.button>
                    <div></div>
                </div>

                {/* Edit Form */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="card shadow-sm"
                    style={{ borderRadius: '12px' }}>

                    <div className="card-body p-4">
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                {/* Product Name */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        اسم المنتج *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="أدخل اسم المنتج"
                                        required
                                        style={{
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                {/* Sub Category */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        الفئة الفرعية *
                                    </label>
                                    <select
                                        className="form-control"
                                        name="sub_category_id"
                                        value={formData.sub_category_id}
                                        onChange={handleInputChange}
                                        required
                                        style={{
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}>
                                        <option value="">اختر الفئة الفرعية</option>
                                        {subCategories.length > 0 ? (
                                            subCategories.map(category => (
                                                <option key={category.id} value={category.id}>
                                                    {category.name || 'فئة بدون اسم'}
                                                </option>
                                            ))
                                        ) : (
                                            <option disabled>جاري تحميل الفئات... ({subCategories.length})</option>
                                        )}
                                    </select>
                                </div>

                                {/* Description */}
                                <div className="col-12 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        وصف المنتج *
                                    </label>
                                    <textarea
                                        className="form-control"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        placeholder="أدخل وصف المنتج"
                                        rows="4"
                                        required
                                        style={{
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>

                                <div className="col-12 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        رابط صورة (اختياري)
                                    </label>
                                    <input
                                        type="url"
                                        className="form-control"
                                        name="image_url"
                                        value={formData.image_url}
                                        onChange={handleInputChange}
                                        placeholder="https://..."
                                        style={{
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                {/* Existing Images */}
                                {existingImages.length > 0 && (
                                    <div className="col-12 mb-3">
                                        <label className="form-label fw-medium" style={{ color: '#333' }}>
                                            الصور الحالية - اختر الصور للحذف
                                        </label>
                                        <div className="row">
                                            {existingImages.map((image, index) => (
                                                <div key={index} className="col-md-2 mb-3">
                                                    <div className="position-relative">
                                                        <img
                                                            src={image.url}
                                                            alt={`Current ${index + 1}`}
                                                            className="img-fluid rounded"
                                                            style={{ 
                                                                width: '100%', 
                                                                height: '80px', 
                                                                objectFit: 'cover',
                                                                border: selectedImagesToDelete.includes(image.id) ? '3px solid #dc3545' : '1px solid #e9ecef',
                                                                borderRadius: '8px'
                                                            }}
                                                        />
                                                        <div className="position-absolute top-0 start-0 p-2">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={selectedImagesToDelete.includes(image.id)}
                                                                onChange={(e) => handleImageDeleteSelection(image.id, e.target.checked)}
                                                                style={{
                                                                    backgroundColor: selectedImagesToDelete.includes(image.id) ? '#dc3545' : 'white',
                                                                    borderColor: '#dc3545',
                                                                    transform: 'scale(1.2)'
                                                                }}
                                                            />
                                                        </div>
                                                        {selectedImagesToDelete.includes(image.id) && (
                                                            <div className="position-absolute top-0 end-0 p-1">
                                                                <span className="badge bg-danger" style={{ fontSize: '10px' }}>
                                                                    للحذف
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <small className="text-muted d-block text-center mt-1">
                                                        صورة {index + 1}
                                                    </small>
                                                </div>
                                            ))}
                                        </div>
                                        {selectedImagesToDelete.length > 0 && (
                                            <div className="mt-2">
                                                <div className="alert alert-warning py-2">
                                                    <small className="text-muted">
                                                        <i className="fas fa-exclamation-triangle mx-1"></i>
                                                        تم اختيار {selectedImagesToDelete.length} صورة للحذف
                                                    </small>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* New Images */}
                                <div className="col-12 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        إضافة صور جديدة
                                    </label>
                                    <div className="mb-3">
                                        <input
                                            type="file"
                                            className="form-control"
                                            accept="image/*"
                                            multiple
                                            onChange={handleImageChange}
                                            style={{
                                                border: '1px solid #e9ecef',
                                                borderRadius: '8px',
                                                padding: '12px 16px',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>
                                    
                                    {formData.images.length > 0 && (
                                        <div className="row">
                                            {formData.images.map((image, index) => (
                                                <div key={index} className="col-md-2 mb-2">
                                                    <div className="position-relative">
                                                        <img
                                                            src={URL.createObjectURL(image)}
                                                            alt={`Preview ${index + 1}`}
                                                            className="img-fluid rounded"
                                                            style={{ width: '100%', height: '80px', objectFit: 'cover' }}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger btn-sm position-absolute top-0 end-0"
                                                            onClick={() => removeImage(index)}
                                                            style={{ padding: '2px 6px', fontSize: '12px' }}>
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Variants */}
                                <div className="col-12 mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <label className="form-label fw-medium mb-0" style={{ color: '#333' }}>
                                            متغيرات المنتج *
                                        </label>
                                        <button
                                            type="button"
                                            className="btn btn-success btn-sm d-flex align-items-center"
                                            onClick={addVariant}
                                            style={{ borderRadius: '6px' }}>
                                            <FaPlus className="mx-1" />
                                            إضافة متغير
                                        </button>
                                    </div>

                                    {variants.map((variant, index) => (
                                        <div key={index} className="border rounded p-3 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="mb-0">المتغير {index + 1}</h6>
                                                {variants.length > 1 && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => removeVariant(index)}
                                                        style={{ borderRadius: '6px' }}>
                                                        <FaTrash />
                                                    </button>
                                                )}
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label fw-medium" style={{ color: '#333' }}>الوحدة *</label>
                                                    <select
                                                        className="form-control"
                                                        value={variant.unit_id}
                                                        onChange={(e) => handleVariantChange(index, 'unit_id', e.target.value)}
                                                        required>
                                                        <option value="">اختر الوحدة</option>
                                                        {units.map(unit => (
                                                            <option key={unit.id} value={unit.id}>{unit.name || unit.title || unit.label}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-md-3 mb-3">
                                                    <label className="form-label fw-medium" style={{ color: '#333' }}>السعر *</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={variant.price}
                                                        onChange={(e) => handleVariantChange(index, 'price', e.target.value)}
                                                        placeholder="السعر"
                                                        required
                                                        min="1"
                                                        step="1"
                                                    />
                                                </div>
                                                <div className="col-md-3 mb-3">
                                                    <label className="form-label fw-medium" style={{ color: '#333' }}>الكمية *</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={variant.quantity}
                                                        onChange={(e) => handleVariantChange(index, 'quantity', e.target.value)}
                                                        placeholder="الكمية"
                                                        required
                                                        min="0"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="d-flex justify-content-center gap-2 mt-4">
                                <motion.button
                                    type="submit"
                                    className="btn btn-primary d-flex align-items-center"
                                    disabled={submitting}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    style={{
                                        backgroundColor: '#184194',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '10px 24px'
                                    }}>
                                    <FaSave className="mx-2" />
                                    {submitting ? 'جاري التعديل...' : 'تعديل المنتج'}
                                </motion.button>

                                <motion.button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => navigate('/pharmacy-products')}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    style={{
                                        borderRadius: '8px',
                                        padding: '10px 24px'
                                    }}>
                                    إلغاء
                                </motion.button>
                            </div>
                        </form>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default PharmacyEditProduct;
